import React, { useState } from 'react';
import { Eye, User, Mail, Phone, Building, FileText, Calendar, Hash, Globe, DollarSign, Check, X, Send, Package, Truck, MapPin, Home, FileSignature } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import { abvigAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, getStatusColor, getStatusLabel } from '../../utils/helpers';
import { sendEmail } from '../../services/emailService';
import toast from 'react-hot-toast';

export default function EquipmentPurchases() {
  const { data, refetch } = useSupabase(abvigAPI.getEquipmentPurchases);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const handleView = (item) => {
    setSelectedPurchase(item);
    setIsModalOpen(true);
    setShowEmailForm(false);
    setEmailSubject(`Regarding Your Equipment Purchase - ${item.transaction_ref || 'Application'}`);
    setEmailMessage(`Dear ${item.buyer_name || 'Sir/Madam'},

Thank you for your equipment purchase application. We have received your request and will review it shortly.

Best regards,
ABVIG Team`);
  };

  const handleStatusUpdate = async (id, status) => {
    setProcessingId(id);
    try {
      await abvigAPI.updateEquipmentPurchaseStatus(id, status);
      toast.success(`Purchase ${status} successfully`);
      refetch();
      if (isModalOpen) {
        setIsModalOpen(false);
        setSelectedPurchase(null);
        setShowEmailForm(false);
      }
    } catch (error) {
      toast.error(`Failed to ${status} purchase`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedPurchase) return;
    
    setIsSendingEmail(true);
    try {
      await sendEmail({
        to: selectedPurchase.buyer_email,
        subject: emailSubject,
        message: emailMessage,
        fromName: process.env.REACT_APP_SENDER_NAME || 'ABVIG Team',
      });
      
      toast.success(`✅ Email sent to ${selectedPurchase.buyer_email}`);
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const renderPurchaseDetails = (purchase) => {
    if (!purchase) return null;

    if (showEmailForm) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">Compose Email</h4>
            <button
              onClick={() => setShowEmailForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={isSendingEmail}
            >
              Back to Details
            </button>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-600">From:</p>
            <p className="text-sm font-medium text-gray-900">{process.env.REACT_APP_SENDER_NAME || 'ABVIG Team'} &lt;{process.env.REACT_APP_SENDER_EMAIL || 'info@abvig.com'}&gt;</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-gray-900">{purchase.buyer_email}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter email subject"
              disabled={isSendingEmail}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows="8"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Type your message here..."
              disabled={isSendingEmail}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailSubject || !emailMessage}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {isSendingEmail ? 'Sending...' : 'Send Email'}
            </button>
            <button
              onClick={() => setShowEmailForm(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSendingEmail}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Package className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Equipment Purchase Order</h3>
                <p className="text-sm text-gray-600">Reference: {purchase.transaction_ref || 'N/A'}</p>
              </div>
            </div>
            <div className="text-right">
              <span className={`badge ${getStatusColor(purchase.status)} text-sm`}>
                {getStatusLabel(purchase.status)}
              </span>
              <p className="text-xs text-gray-500 mt-1">Submitted: {formatDate(purchase.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Buyer Information */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <User className="w-4 h-4" />
              Buyer Information
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Buyer Name</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Building className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Registration No.</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_reg_no || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Home className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Address</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_address || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Country</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_country || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">TIN</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_tin || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Representative</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_representative || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{purchase.buyer_phone || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Seller Information */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Building className="w-4 h-4" />
              Seller Information
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <Building className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Seller Name</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Building className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Registration No.</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_reg_no || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Home className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Address</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_address || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Country</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_country || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">TIN</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_tin || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Representative</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_representative || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{purchase.seller_phone || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FileSignature className="w-4 h-4" />
              Transaction Details
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Transaction Type</p>
                <p className="text-sm font-medium text-gray-900">{purchase.transaction_type || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Date of Issue</p>
                <p className="text-sm font-medium text-gray-900">{purchase.date_of_issue || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">LOI Reference</p>
                <p className="text-sm font-medium text-gray-900">{purchase.loi_ref || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Currency</p>
                <p className="text-sm font-medium text-gray-900">{purchase.currency || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Payment Method</p>
                <p className="text-sm font-medium text-gray-900">{purchase.payment_method || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping & Delivery */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Shipping & Delivery
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Delivery Address</p>
                <p className="text-sm font-medium text-gray-900">{purchase.delivery_address || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Truck className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Port/Terminal</p>
                <p className="text-sm font-medium text-gray-900">{purchase.port_terminal || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Importer Name</p>
                <p className="text-sm font-medium text-gray-900">{purchase.importer_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Importer Country</p>
                <p className="text-sm font-medium text-gray-900">{purchase.importer_country || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Incoterms</p>
                <p className="text-sm font-medium text-gray-900">{purchase.incoterms || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Package className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Equipment Condition</p>
                <p className="text-sm font-medium text-gray-900">{purchase.equipment_condition || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Governing Law</p>
                <p className="text-sm font-medium text-gray-900">{purchase.governing_law || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowEmailForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Send className="w-4 h-4" />
            Respond with Email
          </button>

          {purchase.status === 'pending' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleStatusUpdate(purchase.id, 'approved')}
                disabled={processingId === purchase.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {processingId === purchase.id ? 'Processing...' : 'Approve Purchase'}
              </button>
              <button
                onClick={() => handleStatusUpdate(purchase.id, 'rejected')}
                disabled={processingId === purchase.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                {processingId === purchase.id ? 'Processing...' : 'Reject Purchase'}
              </button>
            </div>
          )}

          {purchase.status !== 'pending' && (
            <div className="pt-2">
              <p className="text-sm text-gray-500 text-center">
                This purchase has been <span className={`font-semibold ${purchase.status === 'approved' ? 'text-green-600' : 'text-red-600'}`}>
                  {getStatusLabel(purchase.status)}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'transaction_ref', label: 'Reference', sortable: true },
    { key: 'buyer_name', label: 'Buyer Name', sortable: true },
    { key: 'buyer_email', label: 'Email' },
    { key: 'transaction_type', label: 'Transaction Type' },
    { key: 'equipment_condition', label: 'Condition' },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Date',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'approved', 'rejected']
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Equipment Purchases</h1>
        <p className="text-gray-600">Manage equipment purchase orders</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} purchases`}
        actions={actions}
        searchPlaceholder="Search purchases..."
        onRowClick={(item) => handleView(item)}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPurchase(null);
          setShowEmailForm(false);
        }}
        title={showEmailForm ? "Compose Email" : "Purchase Details"}
        size="max-w-3xl"
      >
        {renderPurchaseDetails(selectedPurchase)}
      </Modal>
    </div>
  );
}